import React, { useState, useEffect, useRef } from 'react';
import { flushSync } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
    Box, Button, CircularProgress, Stack
} from '@mui/material';
import GoogleIcon from '@mui/icons-material/Google';
import { auth } from '../config/firebase';
import { signInWithPopup, GoogleAuthProvider, signOut } from 'firebase/auth';
import addUserToBackendIfFirstTime from '../utils/addUserToBackendIfFirstTime';
import addUserToFirestoreIfNotExists from '../utils/addUserToFirestoreIfNotExists';
import updateUserInFirestore from '../utils/updateUserInFirestore';
import {
    handlePostAuthRedirect
} from '../components/redirectHandler';

const SignInWithGoogle = ({
    checkUserStatus,
    setSnackbarMessage,
    setSnackbarOpen,
    setIsLogin,
    tokenValue,
    label,
    refreshCredits,
    isManualLoginRef,
    // Optional ref to the box holding the Turnstile widget, scrolled into view
    // when the user has to tick its checkbox.
    humanCheckRef
}) => {
    const navigate = useNavigate();
    const [isVerifying, setIsVerifying] = useState(false);
    const tokenRef = useRef(tokenValue);

    // After the first successful bot verification, skip it on subsequent clicks.
    // The Turnstile token is one-time-use — re-verifying with the same token fails.
    const preVerifiedRef = useRef(false);

    // The in-flight botVerification promise + the token it's verifying. Lets a
    // click that lands mid-verification reuse the same request instead of POSTing
    // the (one-time-use) token twice.
    const verifyPromiseRef = useRef(null);
    const verifyingTokenRef = useRef(null);

    // Prevents multiple concurrent sign-in attempts from rapid clicks.
    // React state updates are async so disabled={isVerifying} alone isn't enough.
    const isSigningInRef = useRef(false);

    // `isManualLoginRef` is an OPTIONAL prop (Home passes one to gate its auth-state
    // effect; SignUp doesn't). Fall back to a local ref so writing `.current` never
    // crashes the sign-in flow when the prop is absent.
    const localManualLoginRef = useRef(false);
    const manualLoginRef = isManualLoginRef || localManualLoginRef;

    useEffect(() => {
        tokenRef.current = tokenValue;
    }, [tokenValue]);

    // Verify a Turnstile token with the backend. Idempotent per token: once a
    // verification succeeds we flip preVerifiedRef and short-circuit; a call that
    // arrives while a verification for the SAME token is in flight reuses that
    // promise (the token is one-time-use and must not be POSTed twice).
    const verifyToken = (token) => {
        if (!token) return Promise.resolve(false);
        if (preVerifiedRef.current) return Promise.resolve(true);
        if (verifyPromiseRef.current && verifyingTokenRef.current === token) {
            return verifyPromiseRef.current;
        }
        verifyingTokenRef.current = token;
        verifyPromiseRef.current = (async () => {
            try {
                const controller = new AbortController();
                const timeoutId = setTimeout(() => controller.abort(), 7000);
                const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}xeo/botVerification`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ Token: token }),
                    signal: controller.signal,
                });
                clearTimeout(timeoutId);
                const resData = await response.json();
                if (resData?.success) {
                    preVerifiedRef.current = true;
                    return true;
                }
                return false; // token rejected — it's spent, so cache the failure
            } catch (verifyError) {
                // Network/timeout: the token may not have been consumed server-side,
                // so clear the cache to allow a retry with the same token.
                verifyPromiseRef.current = null;
                verifyingTokenRef.current = null;
                return false;
            }
        })();
        return verifyPromiseRef.current;
    };

    // NOTE: we deliberately do NOT verify the token proactively on arrival. The
    // Turnstile token is shared with the email/password sign-in path (Home.js),
    // and botVerification consumes it (one-time-use). Verifying it here in the
    // background would break email login. Instead we verify on the actual Google
    // click, concurrently with opening the popup (see below).

    const signInWithGoogle = async () => {
        // Guard against rapid clicks firing multiple concurrent attempts
        if (isSigningInRef.current) return;
        isSigningInRef.current = true;

        const provider = new GoogleAuthProvider();

        // Declared outside try/catch so both blocks can access them
        let popupResolved = false;
        let focusListener = null;

        const cleanupFocusListener = () => {
            if (focusListener) {
                window.removeEventListener('focus', focusListener);
                focusListener = null;
            }
        };

        try {
            manualLoginRef.current = true;
            // Show ONLY the button spinner here — do NOT flip setIsLogin yet.
            flushSync(() => {
                setIsVerifying(true);
            });
            /*
             * NO TOKEN, NO POPUP — and nothing is awaited before the popup.
             *
             * iPhone Safari lets a tap open a window for about a second. This used to
             * wait up to 8s for a Turnstile token before opening Google's popup, so on
             * an iPhone the popup was blocked and the user was told to "allow
             * pop-ups", which an iPhone doesn't offer. Signing in first and checking
             * afterwards is not the answer either: it holds an unverified Google
             * session that Home's auth listener can let into the app. So without a
             * token we don't start at all — point at the check, and the next tap,
             * which has a token, opens Google straight away.
             */
            const tokenAtTap = preVerifiedRef.current ? null : tokenRef.current;
            if (!preVerifiedRef.current && !tokenAtTap) {
                setSnackbarMessage("One moment — we’re checking you’re human. If a “Verify you are human” box shows below, tick it, then tap Sign in with Google again.");
                setSnackbarOpen(true);
                humanCheckRef?.current?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
                return;
            }

            // Detect popup close immediately via window focus event.
            // Firebase takes several seconds on its own to throw auth/popup-closed-by-user.
            focusListener = () => {
                setTimeout(() => {
                    if (!popupResolved) {
                        popupResolved = true;
                        setIsVerifying(false);
                        setIsLogin(false);
                        // Released here so a Google window left open (or just closed)
                        // never blocks the email sign-in on Home, which redirects
                        // through the same auth listener. Safe now that no popup opens
                        // without a token: the bot check started in the tap, alongside
                        // the popup.
                        manualLoginRef.current = false;
                        // Deliberately do NOT clear isSigningInRef here. On a slow
                        // machine this 500ms check can fire before a *successful*
                        // signInWithPopup has set popupResolved; clearing the
                        // re-entry guard then would let a second click start a
                        // concurrent sign-in. The `finally` block always resets
                        // isSigningInRef once the in-flight attempt settles.
                    }
                }, 500);
            };
            window.addEventListener('focus', focusListener, { once: true });

            // Open the popup FIRST, inside the tap, so it appears instantly, and run
            // bot verification CONCURRENTLY (we abort below if it fails). The token
            // is consumed only here, on an actual Google click, so the shared
            // email/password verification path is unaffected.
            const popupPromise = signInWithPopup(auth, provider);
            setIsLogin(true);
            const verifyPromise = preVerifiedRef.current
                ? Promise.resolve(true)
                : verifyToken(tokenAtTap);

            const userCredential = await popupPromise;
            popupResolved = true;
            cleanupFocusListener();

            const verified = await verifyPromise;
            if (!verified) {
                // The popup may have authenticated the user, but the bot check
                // failed — undo the sign-in so an unverified session can't proceed.
                try { await signOut(auth); } catch (_) { /* ignore */ }
                setSnackbarMessage("Security verification failed. Please try again.");
                setSnackbarOpen(true);
                setIsLogin(false);
                return;
            }

            const user = userCredential.user;

            // Add user to Firestore if not exists
            const firestoreRes = await addUserToFirestoreIfNotExists(user.uid, user.email, user.providerData);
            const existed = firestoreRes.existed;
            if (existed) {
                await updateUserInFirestore(user.uid, user.providerData);
            }

            // A pending Shopify connection is attached after sign-in, with
            // the user's say-so — see ShopifyConnectPrompt.

            // Check user status in backend
            const isAllowed = await checkUserStatus(user.uid);
            if (!isAllowed) {
                setIsLogin(false);
                return;
            }

            // Add to backend if first time
            await addUserToBackendIfFirstTime(user.uid, user.email, refreshCredits);

            handlePostAuthRedirect(navigate, true);
        } catch (error) {
            popupResolved = true;
            cleanupFocusListener();
            console.error('Error signing in with Google:', error);

            // User intentionally closed or cancelled the popup — reset silently, no error snackbar
            if (
                error.code === 'auth/popup-closed-by-user' ||
                error.code === 'auth/cancelled-popup-request'
            ) {
                return;
            }

            let errorMessage = "Failed to sign in with Google. Please try again.";
            if (error.code === 'auth/popup-blocked') {
                errorMessage = "Pop-up blocked. Please allow pop-ups and try again.";
            } else if (error.code === 'auth/network-request-failed') {
                errorMessage = "Network error. Please check your connection and try again.";
            }
            setSnackbarMessage(errorMessage);
            setSnackbarOpen(true);
        } finally {
            setIsVerifying(false);
            setIsLogin(false);
            manualLoginRef.current = false;
            isSigningInRef.current = false;
        }
    };

    return (
        <Box>
            <Button
                startIcon={isVerifying ? null : <GoogleIcon />}
                variant="contained"
                color="primary"
                fullWidth
                onClick={signInWithGoogle}
                disabled={isVerifying}
                sx={{
                    textTransform: 'none',
                    fontSize: 16,
                    p: "8px",
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}
            >
                {isVerifying ? (
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <CircularProgress size={16} color="inherit" />
                        <span>Verifying your access...</span>
                    </Stack>
                ) : label}
            </Button>
        </Box>
    );
};

export default SignInWithGoogle;
